import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { openHireStatementPrint } from '@/components/pmv/PrintablePmvCommercial';
import { useHireMasterLabels } from '@/hooks/useHireMasterLabels';
import pmvHireService, {
  formatHireMoney,
  type PmvHireAgreement,
  type PmvHireStatement,
  type PmvHireTimesheet,
} from '@/services/pmv-hire.service';
import {
  HIRE_DOC_STATUS_COLOR,
  formatHireAgreementOption,
  formatHireTimesheetOption,
  hireTableCellEllipsis,
  hireTableLinkBtnStyle,
  resolveLabel,
} from '@/utils/pmvHireUi';

const PmvHireStatementPage: React.FC = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isDetail = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(!!params.get('timesheet'));
  const [form] = Form.useForm();
  const watchedAgreementId = Form.useWatch('hire_agreement_id', form);

  const { supplierLabelById, equipmentLabelById } = useHireMasterLabels();

  const agreementsQ = useQuery({
    queryKey: ['pmv-hire-agreements-for-stmt'],
    queryFn: () => pmvHireService.listAgreements({ limit: 200 }),
    staleTime: 30_000,
  });

  const agreementById = useMemo(() => {
    const m = new Map<string, PmvHireAgreement>();
    for (const a of agreementsQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [agreementsQ.data]);

  const timesheetsQ = useQuery({
    queryKey: ['pmv-hire-ts-for-stmt', watchedAgreementId],
    queryFn: () =>
      pmvHireService.listTimesheets({
        hire_agreement_id: watchedAgreementId,
        status: 'CERTIFIED',
        limit: 100,
      }),
    enabled: createOpen && !!watchedAgreementId,
  });

  useEffect(() => {
    if (!createOpen) return;
    const tsId = params.get('timesheet');
    const agrId = params.get('agreement');
    if (agrId) form.setFieldsValue({ hire_agreement_id: agrId });
    if (tsId) form.setFieldsValue({ timesheet_id: tsId });
  }, [createOpen, form, params]);

  const listQ = useQuery({
    queryKey: ['pmv-hire-statements', status],
    queryFn: () => pmvHireService.listStatements({ status, limit: 100 }),
    enabled: !isDetail,
  });

  const detailQ = useQuery({
    queryKey: ['pmv-hire-statement', id],
    queryFn: () => pmvHireService.getStatement(id!),
    enabled: isDetail,
  });

  const createMut = useMutation({
    mutationFn: (body: { timesheet_id: string; notes?: string }) =>
      pmvHireService.createStatement(body),
    onSuccess: (s) => {
      message.success('Statement created');
      setCreateOpen(false);
      form.resetFields();
      navigate(`/asset/pmv/hire/statements/${s.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const certifyMut = useMutation({
    mutationFn: () => pmvHireService.certifyStatement(id!),
    onSuccess: () => {
      message.success('Statement certified (immutable snapshot)');
      qc.invalidateQueries({ queryKey: ['pmv-hire-statement', id] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-statements'] });
    },
    onError: (e: Error) => message.error(e.message || 'Certify failed'),
  });

  if (isDetail) {
    const s = detailQ.data;
    const agreement = s?.hire_agreement_id ? agreementById.get(s.hire_agreement_id) : undefined;

    return (
      <WorkspaceLayout>
        <EnterprisePageHeader
          documentLabel="Hire Statement"
          documentNumber={s?.statement_number || s?.id?.slice(0, 8)}
          subtitle="Immutable commercial snapshot for invoice reconciliation — not an AP document"
          statusTag={
            s ? (
              <Tag color={HIRE_DOC_STATUS_COLOR[s.status] || 'default'}>{s.status}</Tag>
            ) : undefined
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/hire/statements')}>List</Button>
              <Button
                icon={<PrinterOutlined />}
                disabled={!s}
                onClick={() => {
                  if (!s) return;
                  const agreement = s.hire_agreement_id
                    ? agreementById.get(s.hire_agreement_id)
                    : undefined;
                  openHireStatementPrint({
                    statement: s,
                    agreementLabel: agreement
                      ? formatHireAgreementOption(agreement)
                      : s.hire_agreement_id,
                    supplierLabel: agreement
                      ? resolveLabel(supplierLabelById, agreement.supplier_id)
                      : undefined,
                    equipmentLabel: agreement
                      ? resolveLabel(equipmentLabelById, agreement.pmv_profile_id)
                      : undefined,
                    timesheetLabel: s.timesheet_id
                      ? `Timesheet ${s.timesheet_id.slice(0, 8)}…`
                      : undefined,
                  });
                }}
              >
                Print
              </Button>
              {s?.hire_agreement_id ? (
                <Button
                  onClick={() => navigate(`/asset/pmv/hire/agreements/${s.hire_agreement_id}`)}
                >
                  Agreement
                </Button>
              ) : null}
              {s?.timesheet_id ? (
                <Button onClick={() => navigate(`/asset/pmv/hire/timesheets/${s.timesheet_id}`)}>
                  Timesheet
                </Button>
              ) : null}
              <Button
                type="primary"
                disabled={s?.status === 'CERTIFIED'}
                loading={certifyMut.isPending}
                onClick={() => certifyMut.mutate()}
              >
                Certify
              </Button>
              <Button
                disabled={s?.status !== 'CERTIFIED'}
                onClick={() =>
                  navigate(
                    `/asset/pmv/hire/reconciliations?statement=${s?.id}&agreement=${s?.hire_agreement_id}`
                  )
                }
              >
                Reconcile invoice
              </Button>
            </Space>
          }
        />
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message="Certified statements are immutable snapshots. Later PO rate amendments must not rewrite certified history."
        />
        <Tabs
          items={[
            {
              key: 'header',
              label: 'Header',
              children: (
                <Card size="small">
                  <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                    <Descriptions.Item label="Agreement">
                      {s?.hire_agreement_id ? (
                        <Link to={`/asset/pmv/hire/agreements/${s.hire_agreement_id}`}>
                          {agreement
                            ? formatHireAgreementOption(agreement)
                            : s.hire_agreement_id.slice(0, 8)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Timesheet">
                      {s?.timesheet_id ? (
                        <Link to={`/asset/pmv/hire/timesheets/${s.timesheet_id}`}>
                          Open timesheet
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Equipment">
                      {agreement
                        ? resolveLabel(equipmentLabelById, agreement.pmv_profile_id)
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Supplier">
                      {agreement ? resolveLabel(supplierLabelById, agreement.supplier_id) : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total">
                      {formatHireMoney(s?.total_amount, s?.currency)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">{s?.status}</Descriptions.Item>
                  </Descriptions>
                </Card>
              ),
            },
            {
              key: 'lines',
              label: `Lines (${s?.lines?.length ?? 0})`,
              children: (
                <Card size="small">
                  <Table
                    rowKey={(r, i) => String((r as { id?: string }).id || i)}
                    size="small"
                    dataSource={s?.lines ?? []}
                    pagination={false}
                    locale={{ emptyText: <Empty description="No statement lines" /> }}
                    columns={[
                      { title: 'Description', dataIndex: 'description', ellipsis: true },
                      { title: 'Qty', dataIndex: 'quantity', width: 80 },
                      {
                        title: 'Amount',
                        dataIndex: 'line_amount',
                        render: (v) => formatHireMoney(v, s?.currency),
                      },
                    ]}
                  />
                </Card>
              ),
            },
          ]}
        />
      </WorkspaceLayout>
    );
  }

  const listItems = listQ.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Hire Statements"
        subtitle="Certified commercial snapshots ready for vendor invoice reconciliation"
        primaryActions={
          <Space>
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                setCreateOpen(true);
              }}
            >
              From timesheet
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/reconciliations')}>
              Invoice recon
            </Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={['DRAFT', 'CERTIFIED', 'SUPERSEDED', 'VOID'].map((v) => ({
              value: v,
              label: v,
            }))}
          />
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Statements are created from certified timesheets only. Select masters — no UUID paste."
      />
      {!listQ.isLoading && listItems.length === 0 ? (
        <Empty description="No statements yet">
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create from timesheet
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={listQ.isLoading}
          dataSource={listItems}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 960 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Statement',
              dataIndex: 'statement_number',
              width: 180,
              ellipsis: true,
              render: (v: string, r: PmvHireStatement) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/hire/statements/${r.id}`)}
                  >
                    {v || r.id.slice(0, 8)}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Agreement',
              dataIndex: 'hire_agreement_id',
              width: 200,
              ellipsis: true,
              render: (aid: string) => {
                const a = agreementById.get(aid);
                return (
                  <div style={hireTableCellEllipsis}>
                    {a ? (
                      <Link to={`/asset/pmv/hire/agreements/${aid}`}>{a.agreement_number}</Link>
                    ) : (
                      aid?.slice(0, 8) || '—'
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => (
                <Tag color={HIRE_DOC_STATUS_COLOR[s] || 'default'}>{s}</Tag>
              ),
            },
            {
              title: 'Total',
              dataIndex: 'total_amount',
              width: 140,
              align: 'right' as const,
              render: (v, r) => formatHireMoney(v, r.currency),
            },
          ]}
        />
      )}
      <Modal
        title="Create statement from timesheet"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        width={640}
        destroyOnClose
      >
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Only certified timesheets appear. Statement becomes the expected amount for invoice recon."
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            createMut.mutate({ timesheet_id: v.timesheet_id, notes: v.notes })
          }
        >
          <Form.Item
            name="hire_agreement_id"
            label="Hire agreement"
            rules={[{ required: true, message: 'Select an agreement' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Search agreement"
              options={(agreementsQ.data?.items ?? []).map((a) => ({
                value: a.id,
                label: formatHireAgreementOption(a),
              }))}
              onChange={() => form.setFieldsValue({ timesheet_id: undefined })}
            />
          </Form.Item>
          <Form.Item
            name="timesheet_id"
            label="Certified timesheet"
            rules={[{ required: true, message: 'Select a timesheet' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={
                watchedAgreementId ? 'Select certified timesheet' : 'Select agreement first'
              }
              disabled={!watchedAgreementId}
              loading={timesheetsQ.isLoading}
              options={(timesheetsQ.data?.items ?? []).map((t: PmvHireTimesheet) => ({
                value: t.id,
                label: formatHireTimesheetOption(t),
              }))}
              notFoundContent={
                watchedAgreementId && !timesheetsQ.isLoading
                  ? 'No certified timesheets for this agreement'
                  : undefined
              }
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvHireStatementPage;
